import React, { MouseEventHandler } from 'react'
import classNames from 'classnames'
import { INavigationRoute } from 'transfix-ui/components/NavigationBar/types'
import NavigationItemMenu from 'transfix-ui/components/NavigationItemMenu'
import NotificationBadge from 'transfix-ui/components/NotificationBadge'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useHover } from '../hooks'
import SubRoutesMenu from './SubRoutesMenu'

const NAVBAR_HEIGHT_PX = 48
const LINK_BG_WIDTH_PX = 25
const LINK_BG_WIDTH_HOVER_PX = 32

const useNavigationItemIconStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    position: 'relative',
    height: NAVBAR_HEIGHT_PX,
    padding: 0,
    backgroundColor: brandColors.coolGray8,
    color: brandColors.coolGray4,
    alignItems: 'center',
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    color: brandColors.coolGray4,

    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },

    '&:after': {
      position: 'absolute',
      backgroundColor: brandColors.coolGray8,
      height: 2,
      width: '100%',
      bottom: 0,
      left: 0,
      right: 0,
      content: '""',
    },

    '&:hover': {
      cursor: 'pointer',
      color: brandColors.white,
      textDecoration: 'none',
    },
  },
  linkActive: {
    color: brandColors.white,

    '&:after': {
      position: 'absolute',
      backgroundColor: brandColors.white,
      height: 2,
      width: '100%',
      bottom: 0,
      left: 0,
      right: 0,
      content: '""',
    },
  },
  text: {
    fontWeight: 600,
  },
  linkBackground: {
    display: 'block',
    position: 'absolute',
    width: LINK_BG_WIDTH_PX,
    height: LINK_BG_WIDTH_PX,
    borderRadius: '50%',
    backgroundColor: brandColors.coolGray8,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  hover: {
    [theme.breakpoints.up('lg')]: {
      '& $linkBackground': {
        backgroundColor: brandColors.skyBlue6,
        width: LINK_BG_WIDTH_HOVER_PX,
        height: LINK_BG_WIDTH_HOVER_PX,
      },

      '&:after': {
        backgroundColor: brandColors.white,
      },
    },
  },
  open: {
    '& $linkBackground': {
      backgroundColor: brandColors.skyBlue6,
      width: LINK_BG_WIDTH_HOVER_PX,
      height: LINK_BG_WIDTH_HOVER_PX,
    },

    '&:after': {
      backgroundColor: brandColors.white,
    },
  },
}))

interface INavigationItemIconProps {
  route: INavigationRoute
  /**
   * @default false
   */
  isOpen?: boolean
  className?: string
  onClick?: (name: string) => void
}

const NavigationItemIcon = ({
  className: classNameProp,
  isOpen = false,
  onClick = () => {},
  route,
}: INavigationItemIconProps) => {
  const {
    childRoutes = [],
    disableLink = false,
    Icon,
    isActive,
    location,
    Menu = NavigationItemMenu,
    MenuProps,
    name,
    notificationCount,
  } = route || {}
  const { isDesktop, isMobile } = useBreakpointContext()
  const { isHover, onMouseEnter, onMouseLeave } = useHover()

  const classes = useNavigationItemIconStyles()
  const linkClassName = classNames(classes.link, classNameProp, {
    [classes.linkActive]: !!isActive,
    [classes.hover]: isHover,
    [classes.open]: isOpen,
  })
  const className = classNames(classes.root, classNameProp)

  /**
   * @note Only enable "hover" styles on desktop
   */
  const isMenuOpen = isDesktop ? isHover : isOpen

  const clickHandler: MouseEventHandler<HTMLAnchorElement> = e => {
    if (disableLink) {
      e.preventDefault()
    }

    onClick(name)
  }

  return (
    <li
      data-test='NavigationItemIcon'
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      {Icon && (
        <a
          aria-label='Navigation Item'
          href={location}
          className={linkClassName}
          title={name}
          onClick={clickHandler}>
          <span className={classes.linkBackground} />
          <NotificationBadge notificationCount={notificationCount}>
            <Icon size='large' color='white' />
          </NotificationBadge>
        </a>
      )}
      {MenuProps && <Menu {...MenuProps} isOpen={isMenuOpen} isMobile={isMobile} />}
      {childRoutes && <SubRoutesMenu routes={childRoutes} />}
    </li>
  )
}

export default NavigationItemIcon
